import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { OpportunitiesAction } from "@career/opportunity-list/store/actions/opportunities.actions";
import { Store } from "@ngxs/store";
import { PortalService } from "./portal.service";
import { InvitedOpportunity, Opportunity } from "@career/opportunity-detail/models/opportunity.model";
import { OpportunitiesState } from "@career/opportunity-list/store/states/opportunities.state";
import { UtilsService } from "./utils.service";
import { OpportunitySearchBody } from "@career/opportunity-list/models/opportunities.model";
import { TemplateConfiguration } from "../models/component.model";


@Injectable({
  providedIn: "root",
})
export class JobService {
  constructor(
    protected http: HttpClient,
    protected portalService: PortalService,
    private utils: UtilsService,
    private store: Store
  ) {}

  getCachedSearchFilters() {
    return this.store.selectOnce(OpportunitiesState.filters);
  }
  setCachedSearchFilters(body: OpportunitySearchBody, sort?) {
    return this.store.dispatch(
      new OpportunitiesAction.Filters({ body: body, sort: sort })
    );
  }

  getDepartments(): Observable<any> {
    return this.http.get(
      `/api/portal/${this.portalService.getPortalId()}/departments` +
        "?populate=" +
        encodeURIComponent("manager|parent")
    );
  }

  getLocation(search): Observable<any> {
    return this.http.get("/api/location?search=" + search);
  }

  getDate(date): string {
    let d = new Date(date);
    try {
      return d.toLocaleString("default", {
        month: "long",
        day: "2-digit",
        year: "numeric",
      });
    } catch (err) {
      return d.getMonth() + "/" + d.getDay() + "/" + d.getFullYear();
    }
  }

  getOpps(body = null, sort = null, page = null, config: TemplateConfiguration, size = 10): Observable<any> {
    if (!body) {
      body = {
        agency: this.portalService.getPortalId(),
      };
    } else {
      body.agency = this.portalService.getPortalId();
    }
    let url = `/api/portal/${this.portalService.getPortalId()}/opportunities?populate=${encodeURIComponent(
      "department:name|client:name|labels:value,portalVisibility"
    )}&size=${size}`;
    if (sort) url += "&sort=" + encodeURIComponent(sort);
    if (page) url += "&page=" + encodeURIComponent(page);
    return this.http.post(url, body).pipe(
      map((data: any) => {
        data.opportunities = (data.opportunities || []).map((x) =>
          this.convertToOpp(x, config)
        );
        this.store.dispatch(
          new OpportunitiesAction.Filters({ body: body, sort: sort })
        );
        return data;
      }),
      catchError((err: any) => {
        return throwError(err);
      })
    );
  }

  getOpp(id: any): Observable<Opportunity> {
    return this.http.get<Opportunity>(this.getOppUrl(id)).pipe(
      catchError((err: any) => {
        return throwError(err);
      })
    );
  }

  getInvitedOpp(id: any, token?: string): Observable<InvitedOpportunity> {
    return this.http.get<InvitedOpportunity>(this.getOppUrl(id, token)).pipe(
      catchError((err: any) => {
        return throwError(err);
      })
    );
  }

  getOppUrl(id: string, token?: string): string {
    let url = `/api/portal/${this.portalService.getPortalId()}/opportunity/${id}?populate=${encodeURIComponent(
      "client:name,brand|labels"
    )}`;
    if (token) {
      url += `&token=${encodeURIComponent(token)}`;
    }
    return url;
  }

  convertRate(rate: number) {
    if( rate % 10 > 0 ) {
      return rate.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }
    return rate.toLocaleString();
  }

  convertToOpp(opportunity: Opportunity, config: TemplateConfiguration): Opportunity {
    opportunity.display = { pills: [], summary: null, client: null };
    if (
      opportunity &&
      opportunity.location &&
      opportunity.location.locations &&
      opportunity.location.locations[0]
    ) {
      opportunity.location.locations.forEach((location) => {
        opportunity.display.pills.push({
          name: "LOCATION",
          value: this.utils.createLocationString(location, config['shortLocations']) || "Any Location",
          icon: "location_on",
        });
      });
    }
    if (opportunity && opportunity.location) {
      const relocation = opportunity.location.relocation ? ' - Relocation supported' : '';
      if (!opportunity.location.remote|| (opportunity.location.remote === 'NONE')) {
        opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "In Office" + relocation, icon: "location_on" });
      } else if (opportunity.location.remote === 'PARTIAL') {
        opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "Hybrid" + relocation, icon: "laptop" });
      } else if (opportunity.location.remote === 'FULL') {
        opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "Remote" + relocation, icon: "laptop" });
      } else if (opportunity.location.remote === 'FIELDBASED') {
        opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "Field based" + relocation, icon: "laptop" });
      }
    }

    if (opportunity) {
      if (
        opportunity.employment &&
        opportunity.employment.compensation &&
        opportunity.employment.compensation[0]
      ) {
        if (opportunity.employment.compensation[0].empHours === 'FULLTIME') {
          opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "Full Time", icon: "date_range" });
        } else if (opportunity.employment.compensation[0].empHours === 'PARTTIME') {
          opportunity.display.pills.push({ name: "WORK_ARRANGEMENT", value: "Part Time", icon: "date_range" });
        }
      }}

    //TODO: take locale into account
    if (opportunity) {
      if (
        opportunity.employment &&
        opportunity.employment.compensation &&
        opportunity.employment.compensation[0] &&
        opportunity.employment.compensation[0].rate
      ) {
        const comp = opportunity.employment.compensation[0],
          model = comp.model
            ? `${comp.model.charAt(0).toUpperCase()}${comp.model
                .substring(1)
                .toLowerCase()} `
            : "Salary ",
          amounts = [comp.rate.min, comp.rate.max]
            .filter((value, index, values) => value != null && values.indexOf(value) === index)
            .map((value) => this.convertRate(value))
            .join(" - "),
          currency = comp.currency ? `${comp.currency.toUpperCase()} ` : "",
          rate = model + (amounts ? currency + amounts : "Competitive");
        opportunity.display.pills.push({
          name: "COMPENSATION",
          value: rate,
          icon: "payments",
        });
      }
      if (opportunity.createdDate && config['showPostedDate']) {
        opportunity.display.pills.push({
          name: "POSTED_DATE",
          value: this.getDate(opportunity.createdDate),
          icon: "calendar_today",
        });
      }
      if (opportunity.labels && opportunity.labels.length) {
        opportunity.display.labels = opportunity.labels.map((label) => ({
          color: label?.color || "blue",
          value: label.value,
        }));
      } else {
        opportunity.display.labels = [];
      }
      if( opportunity.externalSummary ) {
        opportunity.display.summary =
          opportunity.externalSummary.substring(0, 300) + "...";
      } else if (opportunity.summary) {
        opportunity.display.summary =
          opportunity.summary.substring(0, 300) + "...";
      }
      if (opportunity.client && opportunity.client.name) {
        opportunity.display.client = opportunity.client.name;
      }
    }

    return opportunity;
  }
}
